import { getSelectedAcademicYear } from '../utils/selectedAcademicYear';
import { supabase } from '../supabaseClient';
import { collectReferencedGradeIdsFromLocalCaches } from './sectionLayoutService';
import { Grade, SchoolGradeRepresentative, Track } from '../types/domain';
import { canWriteSchoolScopedDataCache, invalidateSchoolScopedDataCaches } from '../utils/schoolDataCacheVersion';
import { deleteChallengeProjectsForGrade } from './assignmentChallengeService';
import { deviceCacheStorage, deviceCacheSetItem } from '../utils/deviceCacheStorage';

const GRADE_TRACK_CACHE_VERSION = 'v1';

function gradesCacheKey(workspaceId: string): string {
  return `cp.grades.${GRADE_TRACK_CACHE_VERSION}.${workspaceId}`;
}

function tracksCacheKey(workspaceId: string): string {
  return `cp.tracks.${GRADE_TRACK_CACHE_VERSION}.${workspaceId}`;
}

/** 편제 표시 순서를 유지한 채 tracks LS 캐시를 갱신한다. 실패 시 키 제거 → 다음 getTracks는 DB. */
export function saveTracksCache(workspaceId: string, tracks: Track[]): void {
  if (!workspaceId || typeof window === 'undefined' || !canWriteSchoolScopedDataCache(workspaceId)) return;
  deviceCacheSetItem(tracksCacheKey(workspaceId), JSON.stringify(tracks));
}

function gradeRepresentativesCacheKey(workspaceId: string): string {
  return `cp.gradeRepresentatives.${GRADE_TRACK_CACHE_VERSION}.${workspaceId}`;
}

const pendingGradesBySchool = new Map<string, Promise<Grade[]>>();
const pendingTracksBySchool = new Map<string, Promise<Track[]>>();

/** 마이그레이션 미적용·스키마 캐시에 테이블 없음 등 */
function isSchoolGradeRepresentativesUnavailableError(err: { message?: string; code?: string } | null): boolean {
  if (!err?.message) return false;
  const msg = err.message.toLowerCase();
  if (msg.includes('school_grade_representatives') && msg.includes('schema cache')) return true;
  if (msg.includes('school_grade_representatives') && msg.includes('does not exist')) return true;
  if (err.code === 'PGRST205' || err.code === '42P01') return true;
  return false;
}

/** 네트워크 없이 grades LS만 읽는다 (메인 프레젠 등). */
export function loadGradesCache(workspaceId: string): Grade[] | null {
  if (!workspaceId || typeof window === 'undefined') return null;
  try {
    const cached = deviceCacheStorage.getItem(gradesCacheKey(workspaceId));
    if (!cached) return null;
    const parsed = JSON.parse(cached) as unknown;
    if (Array.isArray(parsed)) return parsed as Grade[];
  } catch {
    /* ignore */
  }
  return null;
}

export type GetGradeTrackRowsOptions = { skipLocalCache?: boolean };

const GRADE_SELECT_BASE = 'id, workspace_id, name, sort_order';
const GRADE_SELECT_WITH_YEAR = 'id, workspace_id, name, sort_order, academic_year';

/** grades.academic_year 마이그레이션 적용 전이면 기존 컬럼만으로 동작 */
function isMissingAcademicYearColumnError(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  if (error.code === '42703' || error.code === 'PGRST204') return true;
  return /academic_year/.test(error.message ?? '');
}

export async function getGrades(
  workspaceId: string,
  options?: GetGradeTrackRowsOptions
): Promise<Grade[]> {
  if (!options?.skipLocalCache && workspaceId && typeof window !== 'undefined') {
    try {
      const cached = deviceCacheStorage.getItem(gradesCacheKey(workspaceId));
      if (cached) {
        const parsed = JSON.parse(cached) as unknown;
        if (Array.isArray(parsed)) return filterGradesBySelectedYear(parsed as Grade[]);
      }
    } catch {
      // ignore parse failure
    }
  }
  const inflightKey = `${workspaceId}:${options?.skipLocalCache ? 'force' : 'def'}`;
  const inflight = pendingGradesBySchool.get(inflightKey);
  if (inflight) return inflight;

  const task = (async () => {
    try {
      const runSelect = (cols: string) =>
        supabase
          .from('grades')
          .select(cols)
          .eq('workspace_id', workspaceId)
          .order('sort_order', { ascending: true })
          .order('name', { ascending: true });
      let { data, error } = await runSelect(GRADE_SELECT_WITH_YEAR);
      if (error && isMissingAcademicYearColumnError(error)) {
        ({ data, error } = await runSelect(GRADE_SELECT_BASE));
      }

      if (error) {
        throw new Error(error.message);
      }
      const rows = (data ?? []) as unknown as Grade[];
      if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
        deviceCacheSetItem(gradesCacheKey(workspaceId), JSON.stringify(rows));
      }
      return rows;
    } finally {
      pendingGradesBySchool.delete(inflightKey);
    }
  })();

  pendingGradesBySchool.set(inflightKey, task);
  return task.then(filterGradesBySelectedYear);
}

/** 전역 학년도 선택에 맞는 클래스만(학년도 컬럼이 없는 행은 그대로 둔다) */
function filterGradesBySelectedYear(rows: Grade[]): Grade[] {
  const year = getSelectedAcademicYear();
  return rows.filter((g) => typeof g.academic_year !== 'number' || g.academic_year === year);
}

export async function createGrade(
  workspaceId: string,
  payload: { name: string; sortOrder?: number; academicYear?: number }
): Promise<Grade> {
  const base = {
    workspace_id: workspaceId,
    name: payload.name.trim(),
    sort_order: payload.sortOrder ?? 0,
  };
  let { data, error } = await supabase
    .from('grades')
    .insert(
      { ...base, academic_year: typeof payload.academicYear === 'number' ? payload.academicYear : getSelectedAcademicYear() }
    )
    .select(GRADE_SELECT_WITH_YEAR)
    .single();
  if (error && isMissingAcademicYearColumnError(error)) {
    ({ data, error } = await supabase.from('grades').insert(base).select(GRADE_SELECT_BASE).single());
  }
  if (error && /grades_workspace_year_name_key|duplicate key/.test(error.message ?? '')) {
    throw new Error('같은 학년도에 같은 이름의 클래스가 이미 있습니다.');
  }
  if (error && /YEAR_NEEDS_PASS/.test(error.message ?? '')) {
    throw new Error('다음 학년도 이용권이 있어야 다음 학년도 클래스를 만들 수 있습니다.');
  }
  if (error && /YEAR_OUT_OF_RANGE/.test(error.message ?? '')) {
    throw new Error('다음 학년도까지만 미리 만들 수 있습니다.');
  }
  if (error && /CLASS_LOCKED/.test(error.message ?? '')) {
    throw new Error('지난 학년도 클래스는 기록이 중지되었습니다.');
  }

  if (error || !data) {
    throw new Error(error?.message ?? '학년 생성에 실패했습니다.');
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrack']);
  return data as unknown as Grade;
}

export async function updateGradeName(gradeId: string, name: string): Promise<void> {
  const { data: row } = await supabase.from('grades').select('workspace_id').eq('id', gradeId).maybeSingle();
  const { error } = await supabase
    .from('grades')
    .update({ name: name.trim() })
    .eq('id', gradeId);
  if (error) {
    if (/CLASS_LOCKED/.test(error.message)) throw new Error('지난 학년도 클래스는 기록이 중지되었습니다.');
    if (/grades_workspace_year_name_key|duplicate key/.test(error.message)) {
      throw new Error('같은 학년도에 같은 이름의 클래스가 이미 있습니다.');
    }
    throw new Error(error.message);
  }
  const workspaceId = (row as { workspace_id?: string } | null)?.workspace_id ?? '';
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrack']);
}

function isMissingRelationError(error: { message?: string; code?: string } | null): boolean {
  if (!error) return false;
  if (error.code === '42P01' || error.code === 'PGRST205') return true;
  const msg = (error.message ?? '').toLowerCase();
  return msg.includes('does not exist') || msg.includes('schema cache');
}

async function deleteRowsByGrade(
  tableName: string,
  workspaceId: string,
  gradeId: string
): Promise<void> {
  const { error } = await supabase.from(tableName).delete().eq('workspace_id', workspaceId).eq('grade_id', gradeId);
  if (error && !isMissingRelationError(error)) throw new Error(error.message);
}

function normalizeSectionArrayWithoutGrade(raw: unknown, gradeId: string): unknown {
  if (!Array.isArray(raw)) return raw;
  const next = raw
    .map((row) => {
      if (!row || typeof row !== 'object') return null;
      const rec = row as Record<string, unknown>;
      const id = typeof rec.id === 'string' ? rec.id : '';
      const gradeIds = Array.isArray(rec.gradeIds)
        ? rec.gradeIds.filter((v): v is string => typeof v === 'string' && v !== gradeId)
        : [];
      if (id === `grade:${gradeId}`) return null;
      const sourceGradeIdRaw = typeof rec.sourceGradeId === 'string' ? rec.sourceGradeId : '';
      const sourceGradeId =
        sourceGradeIdRaw && sourceGradeIdRaw === gradeId ? (gradeIds[0] ?? undefined) : sourceGradeIdRaw || undefined;
      if (id !== 'base' && gradeIds.length === 0) return null;
      return {
        ...rec,
        gradeIds,
        ...(sourceGradeId ? { sourceGradeId } : { sourceGradeId: undefined }),
      };
    })
    .filter((v) => v != null);
  return next;
}

function normalizeClassScheduleSettingsWithoutGrade(raw: unknown, gradeId: string): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const rec = raw as Record<string, unknown>;
  const sections = normalizeSectionArrayWithoutGrade(rec.sections, gradeId);
  const sectionIds = Array.isArray(sections)
    ? new Set(
        sections
          .map((s) => (s && typeof s === 'object' ? (s as Record<string, unknown>).id : ''))
          .filter((id): id is string => typeof id === 'string' && id.length > 0)
      )
    : new Set<string>();
  const sectionRowsRaw =
    rec.sectionRows && typeof rec.sectionRows === 'object' ? (rec.sectionRows as Record<string, unknown>) : {};
  const sectionWeekdayMaxRaw =
    rec.sectionWeekdayMax && typeof rec.sectionWeekdayMax === 'object'
      ? (rec.sectionWeekdayMax as Record<string, unknown>)
      : {};
  const sectionTimeModeRaw =
    rec.sectionTimeMode && typeof rec.sectionTimeMode === 'object'
      ? (rec.sectionTimeMode as Record<string, unknown>)
      : {};
  const sectionWeekdayRowTimesRaw =
    rec.sectionWeekdayRowTimes && typeof rec.sectionWeekdayRowTimes === 'object'
      ? (rec.sectionWeekdayRowTimes as Record<string, unknown>)
      : {};
  const sectionRows = Object.fromEntries(
    Object.entries(sectionRowsRaw).filter(([sectionId]) => sectionIds.has(sectionId))
  );
  const sectionWeekdayMax = Object.fromEntries(
    Object.entries(sectionWeekdayMaxRaw).filter(([sectionId]) => sectionIds.has(sectionId))
  );
  const sectionTimeMode = Object.fromEntries(
    Object.entries(sectionTimeModeRaw).filter(([sectionId]) => sectionIds.has(sectionId))
  );
  const sectionWeekdayRowTimes = Object.fromEntries(
    Object.entries(sectionWeekdayRowTimesRaw).filter(([sectionId]) => sectionIds.has(sectionId))
  );
  return {
    ...rec,
    sections,
    sectionRows,
    sectionWeekdayMax,
    sectionTimeMode,
    sectionWeekdayRowTimes,
  };
}

function normalizeGradeTrackProgramPrefsWithoutGrade(raw: unknown, gradeId: string): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const rec = raw as Record<string, unknown>;
  const modes =
    rec.modes && typeof rec.modes === 'object' && !Array.isArray(rec.modes)
      ? { ...(rec.modes as Record<string, unknown>) }
      : {};
  const syncReps =
    rec.syncReps && typeof rec.syncReps === 'object' && !Array.isArray(rec.syncReps)
      ? { ...(rec.syncReps as Record<string, unknown>) }
      : {};
  const hoursAdjust =
    rec.hoursAdjust && typeof rec.hoursAdjust === 'object' && !Array.isArray(rec.hoursAdjust)
      ? { ...(rec.hoursAdjust as Record<string, unknown>) }
      : {};
  delete modes[gradeId];
  delete syncReps[gradeId];
  delete hoursAdjust[gradeId];
  return {
    ...rec,
    modes,
    syncReps,
    hoursAdjust,
  };
}

function normalizeProgramColorSettingsWithoutGrade(raw: unknown, gradeId: string): unknown {
  if (!raw || typeof raw !== 'object') return raw;
  const rec = raw as Record<string, unknown>;
  const colorsByGrade =
    rec.colorsByGrade && typeof rec.colorsByGrade === 'object' && !Array.isArray(rec.colorsByGrade)
      ? { ...(rec.colorsByGrade as Record<string, unknown>) }
      : {};
  const baseTimetableByGrade =
    rec.baseTimetableByGrade && typeof rec.baseTimetableByGrade === 'object' && !Array.isArray(rec.baseTimetableByGrade)
      ? { ...(rec.baseTimetableByGrade as Record<string, unknown>) }
      : {};
  delete colorsByGrade[gradeId];
  delete baseTimetableByGrade[gradeId];
  return {
    ...rec,
    colorsByGrade,
    baseTimetableByGrade,
  };
}

const GRADE_SCOPED_TABLES = [
  'tracks',
  'school_grade_representatives',
  'annual_timetable_week_memos',
  'annual_timetable_cells',
  'program_progress_cells',
  'program_fixed_content_rows',
  'program_scoped_content_rows',
  'class_members',
  'class_member_groups',
  'weekly_learning_guide_notes',
  'weekly_learning_guide_track_colors',
  'annual_project_programs',
  'base_timetable_cells',
  'daily_timetable_cells',
  'daily_timetable_overrides',
] as const;

type WorkspaceGradeCleanupSource = {
  grade_track_sections?: unknown;
  academic_calendar_sections?: unknown;
  class_schedule_settings?: unknown;
  grade_track_program_preferences?: unknown;
  program_color_settings?: unknown;
} | null;

async function cleanupGradeScopedRows(workspaceId: string, gradeId: string): Promise<void> {
  await Promise.all(GRADE_SCOPED_TABLES.map((tableName) => deleteRowsByGrade(tableName, workspaceId, gradeId)));
}

async function updateWorkspaceGradeConnections(
  workspaceId: string,
  workspaceRow: WorkspaceGradeCleanupSource,
  gradeId: string
): Promise<void> {
  if (!workspaceId || !workspaceRow) return;
  const { error: workspaceUpdateError } = await supabase
    .from('workspaces')
    .update({
      grade_track_sections: normalizeSectionArrayWithoutGrade(workspaceRow.grade_track_sections, gradeId),
      academic_calendar_sections: normalizeSectionArrayWithoutGrade(workspaceRow.academic_calendar_sections, gradeId),
      class_schedule_settings: normalizeClassScheduleSettingsWithoutGrade(workspaceRow.class_schedule_settings, gradeId),
      grade_track_program_preferences: normalizeGradeTrackProgramPrefsWithoutGrade(
        workspaceRow.grade_track_program_preferences,
        gradeId
      ),
      program_color_settings: normalizeProgramColorSettingsWithoutGrade(workspaceRow.program_color_settings, gradeId),
      updated_at: new Date().toISOString(),
    })
    .eq('id', workspaceId);
  if (workspaceUpdateError) throw new Error(workspaceUpdateError.message);
}

async function fetchWorkspaceGradeCleanupSource(workspaceId: string): Promise<WorkspaceGradeCleanupSource> {
  if (!workspaceId) return null;
  const { data, error } = await supabase
    .from('workspaces')
    .select(
      'grade_track_sections, academic_calendar_sections, class_schedule_settings, grade_track_program_preferences, program_color_settings'
    )
    .eq('id', workspaceId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as WorkspaceGradeCleanupSource) ?? null;
}

async function collectGradeIdsFromTable(
  workspaceId: string,
  tableName: string
): Promise<string[]> {
  const { data, error } = await supabase.from(tableName).select('grade_id').eq('workspace_id', workspaceId);
  if (error) {
    if (isMissingRelationError(error)) return [];
    throw new Error(error.message);
  }
  return ((data ?? []) as Array<{ grade_id?: string | null }>)
    .map((row) => (row.grade_id ?? '').trim())
    .filter((gradeId) => gradeId.length > 0);
}

const GRADE_SECTION_ID_PREFIX = 'grade:';

function collectGradeIdsFromSectionArray(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  const ids: string[] = [];
  for (const row of raw) {
    if (!row || typeof row !== 'object') continue;
    const rec = row as Record<string, unknown>;
    const id = typeof rec.id === 'string' ? rec.id.trim() : '';
    if (id.startsWith(GRADE_SECTION_ID_PREFIX)) {
      const gradeId = id.slice(GRADE_SECTION_ID_PREFIX.length).trim();
      if (gradeId) ids.push(gradeId);
    }
    if (Array.isArray(rec.gradeIds)) {
      for (const value of rec.gradeIds) {
        if (typeof value === 'string') {
          const gradeId = value.trim();
          if (gradeId) ids.push(gradeId);
        }
      }
    }
    const sourceGradeId = typeof rec.sourceGradeId === 'string' ? rec.sourceGradeId.trim() : '';
    if (sourceGradeId) ids.push(sourceGradeId);
  }
  return ids;
}

function collectGradeIdsFromGradeTrackProgramPrefs(raw: unknown): string[] {
  if (!raw || typeof raw !== 'object') return [];
  const rec = raw as Record<string, unknown>;
  const modes =
    rec.modes && typeof rec.modes === 'object' && !Array.isArray(rec.modes)
      ? Object.keys(rec.modes as Record<string, unknown>)
      : [];
  const syncReps =
    rec.syncReps && typeof rec.syncReps === 'object' && !Array.isArray(rec.syncReps)
      ? Object.keys(rec.syncReps as Record<string, unknown>)
      : [];
  const hoursAdjust =
    rec.hoursAdjust && typeof rec.hoursAdjust === 'object' && !Array.isArray(rec.hoursAdjust)
      ? Object.keys(rec.hoursAdjust as Record<string, unknown>)
      : [];
  return [...modes, ...syncReps, ...hoursAdjust]
    .map((gradeId) => gradeId.trim())
    .filter((gradeId) => gradeId.length > 0);
}

function collectGradeIdsFromProgramColorSettings(raw: unknown): string[] {
  if (!raw || typeof raw !== 'object') return [];
  const rec = raw as Record<string, unknown>;
  const colorsByGrade =
    rec.colorsByGrade && typeof rec.colorsByGrade === 'object' && !Array.isArray(rec.colorsByGrade)
      ? Object.keys(rec.colorsByGrade as Record<string, unknown>)
      : [];
  const baseTimetableByGrade =
    rec.baseTimetableByGrade && typeof rec.baseTimetableByGrade === 'object' && !Array.isArray(rec.baseTimetableByGrade)
      ? Object.keys(rec.baseTimetableByGrade as Record<string, unknown>)
      : [];
  return [...colorsByGrade, ...baseTimetableByGrade]
    .map((gradeId) => gradeId.trim())
    .filter((gradeId) => gradeId.length > 0);
}

function collectOrphanGradeIdsFromWorkspaceSettings(
  workspaceRow: WorkspaceGradeCleanupSource,
  liveGradeIds: Set<string>
): string[] {
  if (!workspaceRow) return [];
  const classScheduleSections =
    workspaceRow.class_schedule_settings &&
    typeof workspaceRow.class_schedule_settings === 'object' &&
    !Array.isArray(workspaceRow.class_schedule_settings)
      ? (workspaceRow.class_schedule_settings as Record<string, unknown>).sections
      : undefined;
  const candidateIds = [
    ...collectGradeIdsFromSectionArray(workspaceRow.grade_track_sections),
    ...collectGradeIdsFromSectionArray(workspaceRow.academic_calendar_sections),
    ...collectGradeIdsFromSectionArray(classScheduleSections),
    ...collectGradeIdsFromGradeTrackProgramPrefs(workspaceRow.grade_track_program_preferences),
    ...collectGradeIdsFromProgramColorSettings(workspaceRow.program_color_settings),
  ];
  const orphanIds = new Set<string>();
  candidateIds.forEach((gradeId) => {
    if (!liveGradeIds.has(gradeId)) orphanIds.add(gradeId);
  });
  return Array.from(orphanIds);
}

export async function deleteGrade(gradeId: string): Promise<void> {
  const { data: row } = await supabase
    .from('grades')
    .select(
      'workspace_id, workspaces:workspace_id(grade_track_sections, academic_calendar_sections, class_schedule_settings, grade_track_program_preferences, program_color_settings)'
    )
    .eq('id', gradeId)
    .maybeSingle();
  const workspaceId = (row as { workspace_id?: string } | null)?.workspace_id ?? '';
  const { data: tracksInGrade } = await supabase
    .from('tracks')
    .select('id')
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (workspaceId) {
    // 사진(스토리지)·용량 반환은 DB cascade 로 처리되지 않음
    await deleteChallengeProjectsForGrade(workspaceId, gradeId);
  }
  const { error } = await supabase.from('grades').delete().eq('id', gradeId);
  if (error) {
    throw new Error(error.message);
  }
  const trackIds = ((tracksInGrade ?? []) as Array<{ id: string }>).map((t) => t.id).filter(Boolean);
  if (trackIds.length > 0) {
    const { error: tracksError } = await supabase
      .from('tracks')
      .delete()
      .eq('workspace_id', workspaceId)
      .in('id', trackIds);
    if (tracksError) throw new Error(tracksError.message);
  }
  const workspacesRel = (row as { workspaces?: unknown } | null)?.workspaces;
  const workspaceRowRaw = Array.isArray(workspacesRel) ? workspacesRel[0] : workspacesRel;
  const workspaceRow =
    (workspaceRowRaw as WorkspaceGradeCleanupSource) ?? null;
  await cleanupGradeScopedRows(workspaceId, gradeId);
  await updateWorkspaceGradeConnections(workspaceId, workspaceRow, gradeId);
  await invalidateSchoolScopedDataCaches(workspaceId);
}

export async function getOrphanGradeIds(workspaceId: string): Promise<string[]> {
  if (!workspaceId) return [];
  const [gradeRows, workspaceRow, ...gradeIdLists] = await Promise.all([
    supabase.from('grades').select('id').eq('workspace_id', workspaceId),
    fetchWorkspaceGradeCleanupSource(workspaceId),
    ...GRADE_SCOPED_TABLES.map((tableName) => collectGradeIdsFromTable(workspaceId, tableName)),
  ]);
  if (gradeRows.error) throw new Error(gradeRows.error.message);
  const liveGradeIds = new Set(
    ((gradeRows.data ?? []) as Array<{ id?: string }>)
      .map((row) => (row.id ?? '').trim())
      .filter((id) => id.length > 0)
  );
  const orphanIds = new Set<string>();
  gradeIdLists.flat().forEach((gradeId) => {
    if (!liveGradeIds.has(gradeId)) orphanIds.add(gradeId);
  });
  collectOrphanGradeIdsFromWorkspaceSettings(workspaceRow, liveGradeIds).forEach((gradeId) => {
    orphanIds.add(gradeId);
  });
  if (typeof window !== 'undefined') {
    collectReferencedGradeIdsFromLocalCaches(workspaceId).forEach((gradeId) => {
      if (!liveGradeIds.has(gradeId)) orphanIds.add(gradeId);
    });
  }
  return Array.from(orphanIds).sort((a, b) => a.localeCompare(b));
}

export async function purgeOrphanGradeData(workspaceId: string, orphanGradeId: string): Promise<void> {
  const gradeId = orphanGradeId.trim();
  if (!workspaceId || !gradeId) return;
  const workspaceRow = await fetchWorkspaceGradeCleanupSource(workspaceId);
  await cleanupGradeScopedRows(workspaceId, gradeId);
  await updateWorkspaceGradeConnections(workspaceId, workspaceRow, gradeId);
  await invalidateSchoolScopedDataCaches(workspaceId);
}

export async function getSchoolGradeRepresentatives(
  workspaceId: string
): Promise<SchoolGradeRepresentative[]> {
  if (workspaceId && typeof window !== 'undefined') {
    try {
      const cached = deviceCacheStorage.getItem(gradeRepresentativesCacheKey(workspaceId));
      if (cached) {
        const parsed = JSON.parse(cached) as unknown;
        if (Array.isArray(parsed)) return parsed as SchoolGradeRepresentative[];
      }
    } catch {
      // ignore parse failure
    }
  }
  const { data, error } = await supabase
    .from('school_grade_representatives')
    .select('id, workspace_id, grade_number, grade_id, created_at, updated_at')
    .eq('workspace_id', workspaceId)
    .order('grade_number', { ascending: true });
  if (error) {
    if (isSchoolGradeRepresentativesUnavailableError(error)) {
      return [];
    }
    throw new Error(error.message);
  }
  const rows = (data ?? []) as SchoolGradeRepresentative[];
  if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
    deviceCacheSetItem(
      gradeRepresentativesCacheKey(workspaceId),
      JSON.stringify(rows)
    );
  }
  return rows;
}

export async function setSchoolGradeRepresentative(
  workspaceId: string,
  gradeNumber: number,
  gradeId: string
): Promise<SchoolGradeRepresentative> {
  const safeNumber = Math.min(6, Math.max(1, Math.trunc(gradeNumber)));
  const { data, error } = await supabase
    .from('school_grade_representatives')
    .upsert(
      {
        workspace_id: workspaceId,
        grade_number: safeNumber,
        grade_id: gradeId,
      },
      { onConflict: 'workspace_id,grade_number' }
    )
    .select('id, workspace_id, grade_number, grade_id, created_at, updated_at')
    .single();
  if (error || !data) {
    if (isSchoolGradeRepresentativesUnavailableError(error)) {
      throw new Error(
        '대표 클래스 저장을 위해 DB에 school_grade_representatives 테이블이 필요합니다. Supabase 마이그레이션을 적용해 주세요.'
      );
    }
    throw new Error(error?.message ?? '대표 클래스 저장에 실패했습니다.');
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrack']);
  return data as SchoolGradeRepresentative;
}

export async function deleteSchoolGradeRepresentative(workspaceId: string, gradeNumber: number): Promise<void> {
  const safeNumber = Math.min(6, Math.max(1, Math.trunc(gradeNumber)));
  const { error } = await supabase
    .from('school_grade_representatives')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_number', safeNumber);
  if (error) {
    if (isSchoolGradeRepresentativesUnavailableError(error)) {
      return;
    }
    throw new Error(error.message);
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrack']);
}

export async function getTracks(
  workspaceId: string,
  options?: GetGradeTrackRowsOptions
): Promise<Track[]> {
  if (!options?.skipLocalCache && workspaceId && typeof window !== 'undefined') {
    try {
      const cached = deviceCacheStorage.getItem(tracksCacheKey(workspaceId));
      if (cached) {
        const parsed = JSON.parse(cached) as unknown;
        if (Array.isArray(parsed)) return parsed as Track[];
      }
    } catch {
      // ignore parse failure
    }
  }
  const inflightKey = `${workspaceId}:${options?.skipLocalCache ? 'force' : 'def'}`;
  const inflight = pendingTracksBySchool.get(inflightKey);
  if (inflight) return inflight;

  const task = (async () => {
    try {
      const { data, error } = await supabase
        .from('tracks')
        .select('id, workspace_id, grade_id, name, description, criteria_hours, track_category')
        .eq('workspace_id', workspaceId)
        .order('name', { ascending: true });

      if (error) {
        throw new Error(error.message);
      }
      const rows = (data ?? []) as Track[];
      if (workspaceId && typeof window !== 'undefined' && canWriteSchoolScopedDataCache(workspaceId)) {
        deviceCacheSetItem(tracksCacheKey(workspaceId), JSON.stringify(rows));
      }
      return rows;
    } finally {
      pendingTracksBySchool.delete(inflightKey);
    }
  })();

  pendingTracksBySchool.set(inflightKey, task);
  return task;
}

export async function createTrack(
  workspaceId: string,
  payload: {
    gradeId: string | null;
    name: string;
    description?: string;
    criteriaHours?: number;
    trackCategory?: 'regular' | 'other';
  }
): Promise<Track> {
  const hours = payload.criteriaHours ?? 0;
  const { data, error } = await supabase
    .from('tracks')
    .insert({
      workspace_id: workspaceId,
      grade_id: payload.gradeId,
      name: payload.name.trim(),
      description: payload.description?.trim() || null,
      criteria_hours: Math.min(999, Math.max(0, hours)),
      track_category: payload.trackCategory === 'other' ? 'other' : 'regular',
    })
    .select('id, workspace_id, grade_id, name, description, criteria_hours, track_category')
    .single();

  if (error || !data) {
    throw new Error(error?.message ?? '편제 생성에 실패했습니다.');
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrack']);
  return data as Track;
}

export async function updateTrackGroup(
  workspaceId: string,
  tracks: Track[],
  newName: string,
  newAliases: string[],
  criteriaHours?: number,
  trackCategory?: 'regular' | 'other'
): Promise<void> {
  if (tracks.length === 0) return;
  const cleaned = newAliases.map((a) => a.trim()).filter((a) => a.length > 0);
  if (cleaned.length === 0) {
    throw new Error('시간표명은 최소 1개 이상이어야 합니다.');
  }

  const base = tracks[0];
  const name = newName.trim();
  const hours = criteriaHours ?? base.criteria_hours ?? 0;
  const safeHours = Math.min(999, Math.max(0, hours));
  const safeTrackCategory: 'regular' | 'other' =
    trackCategory ?? (base.track_category === 'other' ? 'other' : 'regular');

  const existingCount = Math.min(tracks.length, cleaned.length);
  const rowsToUpdate = tracks.slice(0, existingCount).map((t, idx) => ({
    id: t.id,
    workspace_id: workspaceId,
    grade_id: t.grade_id,
    name,
    description: cleaned[idx],
    criteria_hours: safeHours,
    track_category: safeTrackCategory,
  }));

  const rowsToInsert =
    cleaned.length > tracks.length
      ? cleaned.slice(tracks.length).map((alias) => ({
          workspace_id: workspaceId,
          grade_id: base.grade_id,
          name,
          description: alias,
          criteria_hours: safeHours,
          track_category: safeTrackCategory,
        }))
      : [];

  const idsToDelete =
    cleaned.length < tracks.length ? tracks.slice(cleaned.length).map((t) => t.id) : [];

  if (rowsToUpdate.length > 0) {
    const { error } = await supabase.from('tracks').upsert(rowsToUpdate, { onConflict: 'id' });
    if (error) {
      throw new Error(error.message);
    }
  }

  if (rowsToInsert.length > 0) {
    const { error } = await supabase.from('tracks').insert(rowsToInsert);
    if (error) {
      throw new Error(error.message);
    }
  }

  if (idsToDelete.length > 0) {
    const { error } = await supabase
      .from('tracks')
      .delete()
      .eq('workspace_id', workspaceId)
      .in('id', idsToDelete);
    if (error) {
      throw new Error(error.message);
    }
  }
  await invalidateSchoolScopedDataCaches(workspaceId, ['gradeTrack']);
}

export async function deleteTrackGroup(workspaceId: string, tracks: Track[]): Promise<void> {
  const ids = tracks.map((t) => t.id);
  if (ids.length === 0) return;
  const { error } = await supabase
    .from('tracks')
    .delete()
    .eq('workspace_id', workspaceId)
    .in('id', ids);
  if (error) {
    throw new Error(error.message);
  }
  await invalidateSchoolScopedDataCaches(workspaceId);
}

/** 저장 시 캐시된 id 목록 없이 해당 클래스(grade)의 편제 행을 전부 삭제 */
export async function deleteTracksForGrade(workspaceId: string, gradeId: string): Promise<void> {
  if (!gradeId.trim()) return;
  const { error } = await supabase
    .from('tracks')
    .delete()
    .eq('workspace_id', workspaceId)
    .eq('grade_id', gradeId);
  if (error) {
    throw new Error(error.message);
  }
  await invalidateSchoolScopedDataCaches(workspaceId);
}

